import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { triggerHaptic } from '@/utils/mobile';

const buttonVariants = {
  default:
    'bg-forest-green text-white hover:bg-forest-green-light shadow-sm hover:shadow-md hover:-translate-y-px active:translate-y-0 dark:bg-accent-primary dark:hover:bg-accent-primary dark:text-on-brand dark:rounded-pill dark:border-0 dark:shadow-none dark:hover:shadow-[var(--glow-md)]',
  secondary:
    'bg-transparent border-2 border-forest-green text-forest-green hover:bg-forest-green/10 dark:border-border-soft dark:text-text-primary dark:bg-white/[0.045] dark:border dark:hover:bg-bg-elevated',
  success:
    'bg-accent-success text-on-brand hover:opacity-90 dark:rounded-pill',
  outline:
    'border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white dark:border-border-soft dark:text-text-primary dark:hover:bg-bg-elevated dark:hover:text-text-primary',
  ghost:
    'hover:bg-forest-green/10 hover:text-forest-green dark:hover:bg-white/5 dark:hover:text-text-primary',
  destructive: 'bg-burgundy text-white hover:bg-burgundy-light',
};

const buttonSizes = {
  default: 'min-h-[var(--touch-target-min)] px-4 py-2 text-base sm:text-sm',
  sm: 'min-h-[2.5rem] px-3 py-1.5 text-sm',
  lg: 'min-h-[3rem] px-6 py-3 text-lg',
  icon: 'min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] p-2',
};

export const Button = React.forwardRef(function Button(
  {
    className = '',
    variant = 'default',
    size = 'default',
    children = null,
    disabled = false,
    loading = false,
    type = 'button',
    haptic = undefined,
    onClick,
    'aria-busy': ariaBusy,
    ...props
  } = {},
  ref
) {
  const isBusy = Boolean(loading);
  const showSpinner = isBusy;

  const handleClick = (e) => {
    if (haptic && !disabled && !isBusy) {
      void triggerHaptic(haptic === true ? 'medium' : haptic);
    }
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] font-medium',
        '[transition:var(--transition-interactive)]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-green dark:focus-visible:outline-accent-primary',
        'disabled:opacity-50 disabled:pointer-events-none',
        buttonVariants[variant] || buttonVariants.default,
        buttonSizes[size] || buttonSizes.default,
        className
      )}
      disabled={disabled || isBusy}
      aria-busy={ariaBusy ?? isBusy}
      onClick={handleClick}
      {...props}
    >
      {showSpinner && (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      )}
      {children}
    </button>
  );
});

Button.displayName = 'Button';
